import { useEffect, useState } from "react";
import { Plus, ArrowsClockwise, Trash, Eye, ArrowRight } from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

function CreateJobModal({ open, onClose, onCreated }) {
    const [name, setName] = useState("");
    const [domainText, setDomainText] = useState("");
    const [gd, setGd] = useState("");
    const [nc, setNc] = useState("");
    const [forcedText, setForcedText] = useState("");
    const [busy, setBusy] = useState(false);
    const [preview, setPreview] = useState(null);
    if (!open) return null;

    const parseList = (t) => t.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);

    const doPreview = async () => {
        setBusy(true);
        setPreview(null);
        try {
            const { data } = await api.post("/migrations/preview", {
                domains: parseList(domainText),
                gd_accounts: parseList(gd),
                nc_accounts: parseList(nc),
                forced_gd: parseList(forcedText),
            });
            setPreview(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const doCreate = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/migrations", {
                name,
                domains: parseList(domainText),
                gd_accounts: parseList(gd),
                nc_accounts: parseList(nc),
                forced_gd: parseList(forcedText),
            });
            toast.success(`Job created: ${data.name}`);
            onCreated();
            onClose();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(15,23,45,0.35)", backdropFilter: "blur(6px)" }}
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="glass-strong w-full max-w-4xl p-8 max-h-[90vh] overflow-auto"
                data-testid="migration-modal"
            >
                <h2
                    className="text-2xl font-extrabold mb-6"
                    style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
                >
                    New migration
                </h2>

                <div className="grid grid-cols-2 gap-4">
                    <label className="col-span-2">
                        <span className="text-xs font-semibold text-[color:var(--dm-text-2)] mb-1 block">
                            Job name
                        </span>
                        <input
                            data-testid="migration-name-input"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="glass-input"
                            placeholder="e.g. Q1 2026 batch"
                        />
                    </label>
                    <label className="col-span-2">
                        <span className="text-xs font-semibold text-[color:var(--dm-text-2)] mb-1 block">
                            Domains (one per line, or comma-separated)
                        </span>
                        <textarea
                            data-testid="migration-domains-input"
                            value={domainText}
                            onChange={(e) => setDomainText(e.target.value)}
                            className="glass-input mono !min-h-[180px]"
                            placeholder={"foo.com\nbar.io\nbaz.net"}
                        />
                        <span className="text-xs text-[color:var(--dm-text-2)] mt-1 block">
                            {parseList(domainText).length} domains
                        </span>
                    </label>
                    <label>
                        <span className="text-xs font-semibold text-[color:var(--dm-text-2)] mb-1 block">
                            GoDaddy destinations (comma-separated)
                        </span>
                        <input
                            data-testid="migration-gd-input"
                            value={gd}
                            onChange={(e) => setGd(e.target.value)}
                            className="glass-input mono"
                            placeholder="gd_new_a, gd_new_b, gd_new_c"
                        />
                    </label>
                    <label>
                        <span className="text-xs font-semibold text-[color:var(--dm-text-2)] mb-1 block">
                            Namecheap destinations (comma-separated)
                        </span>
                        <input
                            data-testid="migration-nc-input"
                            value={nc}
                            onChange={(e) => setNc(e.target.value)}
                            className="glass-input mono"
                            placeholder="nc_account_a, nc_account_b"
                        />
                    </label>
                    <label className="col-span-2">
                        <span className="text-xs font-semibold text-[color:var(--dm-text-2)] mb-1 block">
                            Force to GoDaddy (optional — e.g., .in TLDs, NC-unsupported)
                        </span>
                        <input
                            value={forcedText}
                            onChange={(e) => setForcedText(e.target.value)}
                            className="glass-input mono"
                            placeholder="stubborn.co, weird.tld"
                        />
                    </label>
                </div>

                <div className="mt-6 flex gap-2">
                    <button
                        onClick={doPreview}
                        disabled={busy || !domainText.trim()}
                        className="btn-ghost"
                        data-testid="migration-preview-button"
                    >
                        {busy ? "Routing…" : "Preview routing"}
                    </button>
                    <button
                        onClick={doCreate}
                        disabled={busy || !name.trim() || !domainText.trim()}
                        className="btn-glossy"
                        data-testid="migration-create-button"
                    >
                        Create job
                    </button>
                </div>

                {preview && (
                    <div className="glass !rounded-xl p-4 mt-5">
                        <div className="text-sm font-bold mb-3">Preview</div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                            <div>
                                <div className="text-[color:var(--dm-text-2)]">Total input</div>
                                <div
                                    className="text-lg font-bold"
                                    style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
                                >
                                    {preview.stats.total_input}
                                </div>
                            </div>
                            <div>
                                <div className="text-[color:var(--dm-text-2)]">.in skipped</div>
                                <div
                                    className="text-lg font-bold"
                                    style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
                                >
                                    {preview.stats.in_skipped}
                                </div>
                            </div>
                            <div>
                                <div className="text-[color:var(--dm-text-2)]">Forced GD</div>
                                <div
                                    className="text-lg font-bold"
                                    style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
                                >
                                    {preview.stats.forced_gd}
                                </div>
                            </div>
                            <div>
                                <div className="text-[color:var(--dm-text-2)]">Even-split Δ</div>
                                <div
                                    className="text-lg font-bold"
                                    style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
                                >
                                    {preview.stats.even_split_delta}
                                </div>
                            </div>
                        </div>
                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                            {Object.entries(preview.stats.counts || {}).map(([a, c]) => (
                                <div
                                    key={a}
                                    className="flex items-center justify-between text-xs bg-[rgba(0,122,255,0.05)] px-3 py-2 rounded-lg"
                                >
                                    <code className="mono">{a}</code>
                                    <span className="font-bold">{c}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function JobDetail({ job, onClose }) {
    if (!job) return null;
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(15,23,45,0.35)", backdropFilter: "blur(6px)" }}
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="glass-strong w-full max-w-4xl p-8 max-h-[90vh] overflow-auto"
                data-testid="migration-detail"
            >
                <h2
                    className="text-2xl font-extrabold mb-2"
                    style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
                >
                    {job.name}
                </h2>
                <div className="flex gap-2 items-center mb-4">
                    <span className="pill pill-brand">{job.status}</span>
                    <span className="text-xs text-[color:var(--dm-text-2)]">
                        {job.created_at && new Date(job.created_at).toLocaleString()}
                    </span>
                </div>
                <div className="text-xs mb-4 space-y-1">
                    <div>
                        <span className="text-[color:var(--dm-text-2)]">GD:</span>{" "}
                        <code className="mono">{(job.gd_accounts || []).join(", ") || "—"}</code>
                    </div>
                    <div>
                        <span className="text-[color:var(--dm-text-2)]">NC:</span>{" "}
                        <code className="mono">{(job.nc_accounts || []).join(", ") || "—"}</code>
                    </div>
                </div>
                <div className="glass !rounded-xl p-3 mb-4">
                    <div className="text-xs uppercase font-bold text-[color:var(--dm-text-2)] mb-2">
                        Routing summary
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                        {Object.entries(job.stats?.counts || {}).map(([a, c]) => (
                            <div key={a} className="bg-[rgba(0,122,255,0.06)] px-3 py-2 rounded-lg">
                                <div className="mono text-xs">{a}</div>
                                <div className="text-lg font-bold">{c}</div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="text-xs uppercase font-bold text-[color:var(--dm-text-2)] mb-2">
                    Assignments ({job.assignments?.length || 0})
                </div>
                <div className="glass !rounded-xl max-h-96 overflow-auto text-xs">
                    <div className="grid grid-cols-4 gap-2 px-3 py-2 font-bold bg-[rgba(0,122,255,0.05)] sticky top-0">
                        <div>Domain</div>
                        <div>Destination</div>
                        <div>Account</div>
                        <div>Brand stem</div>
                    </div>
                    {job.assignments?.map((a, i) => (
                        <div
                            key={i}
                            className="grid grid-cols-4 gap-2 px-3 py-1.5 border-b border-[rgba(15,41,89,0.05)]"
                        >
                            <code className="mono">{a.domain}</code>
                            <span>
                                {a.destination_type ? (
                                    <span
                                        className={`pill ${
                                            a.destination_type === "godaddy" ? "pill-warn" : "pill-brand"
                                        }`}
                                    >
                                        {a.destination_type}
                                    </span>
                                ) : (
                                    <span className="pill pill-neutral" title={a.reason}>
                                        skipped
                                    </span>
                                )}
                            </span>
                            <code className="mono">{a.destination_account || "—"}</code>
                            <code className="mono text-[color:var(--dm-text-2)]">{a.stem_loose}</code>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default function MigrationsPage() {
    const [jobs, setJobs] = useState([]);
    const [openCreate, setOpenCreate] = useState(false);
    const [detail, setDetail] = useState(null);

    const load = async () => {
        try {
            const { data } = await api.get("/migrations");
            setJobs(data);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    useEffect(() => {
        load();
    }, []);

    const del = async (id) => {
        if (!confirm("Delete this job?")) return;
        try {
            await api.delete(`/migrations/${id}`);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const setStatus = async (id, status) => {
        try {
            await api.patch(`/migrations/${id}/status`, { status });
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div className="space-y-4" data-testid="migrations-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <h1
                        className="text-3xl font-extrabold"
                        style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
                    >
                        Migrations
                    </h1>
                    <p className="text-sm text-[color:var(--dm-text-2)] mt-1">
                        Route a batch of domains across your GoDaddy + Namecheap accounts with brand spread.
                    </p>
                </div>
                <button
                    onClick={() => setOpenCreate(true)}
                    className="btn-glossy"
                    data-testid="new-migration-button"
                >
                    <Plus size={16} weight="bold" /> New migration
                </button>
            </div>

            {jobs.length === 0 ? (
                <div className="glass p-10 text-center">
                    <ArrowsClockwise size={40} weight="duotone" className="mx-auto text-[color:var(--dm-brand)] mb-3" />
                    <div className="text-lg font-bold mb-1">No migrations yet</div>
                    <div className="text-sm text-[color:var(--dm-text-2)] mb-4">
                        Paste your domain list and route them evenly across accounts in seconds.
                    </div>
                    <button onClick={() => setOpenCreate(true)} className="btn-glossy">
                        <Plus size={16} weight="bold" /> New migration
                    </button>
                </div>
            ) : (
                <div className="glass overflow-hidden">
                    <div
                        className="table-row sticky top-0 z-10 bg-white/85 backdrop-blur-md text-[0.7rem] uppercase tracking-wider text-[color:var(--dm-text-2)] font-bold"
                        style={{ gridTemplateColumns: "2fr 0.9fr 1fr 1.2fr 1.5fr" }}
                    >
                        <div>Name</div>
                        <div>Status</div>
                        <div>Domains</div>
                        <div>Created</div>
                        <div className="text-right">Actions</div>
                    </div>
                    <div className="max-h-[70vh] overflow-y-auto">
                    {jobs.map((j) => (
                        <div
                            key={j.id}
                            className="table-row"
                            data-testid={`migration-row-${j.name}`}
                            style={{ gridTemplateColumns: "2fr 0.9fr 1fr 1.2fr 1.5fr" }}
                        >
                            <div className="font-semibold text-sm">{j.name}</div>
                            <div>
                                <span
                                    className={`pill ${
                                        j.status === "completed"
                                            ? "pill-ok"
                                            : j.status === "failed"
                                              ? "pill-danger"
                                              : j.status === "running"
                                                ? "pill-brand"
                                                : "pill-neutral"
                                    }`}
                                >
                                    {j.status}
                                </span>
                            </div>
                            <div className="text-sm mono">{j.assignments?.length || 0}</div>
                            <div className="text-xs mono text-[color:var(--dm-text-2)]">
                                {j.created_at && new Date(j.created_at).toLocaleString()}
                            </div>
                            <div className="text-right flex gap-1 justify-end">
                                <button
                                    onClick={() => setDetail(j)}
                                    className="btn-ghost !py-1 !px-2 text-xs"
                                    data-testid={`view-migration-${j.name}`}
                                >
                                    <Eye size={14} weight="duotone" />
                                </button>
                                {j.status === "queued" && (
                                    <button
                                        onClick={() => setStatus(j.id, "running")}
                                        className="btn-ghost !py-1 !px-2 text-xs"
                                    >
                                        <ArrowRight size={14} weight="bold" /> Start
                                    </button>
                                )}
                                {j.status === "running" && (
                                    <button
                                        onClick={() => setStatus(j.id, "completed")}
                                        className="btn-ghost !py-1 !px-2 text-xs"
                                    >
                                        Mark done
                                    </button>
                                )}
                                <button
                                    onClick={() => del(j.id)}
                                    className="btn-ghost btn-danger !py-1 !px-2 text-xs"
                                    data-testid={`delete-migration-${j.name}`}
                                >
                                    <Trash size={14} weight="duotone" />
                                </button>
                            </div>
                        </div>
                    ))}
                    </div>
                </div>
            )}

            <CreateJobModal
                open={openCreate}
                onClose={() => setOpenCreate(false)}
                onCreated={load}
            />
            <JobDetail job={detail} onClose={() => setDetail(null)} />
        </div>
    );
}
